import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks'
import { currentUserSelect } from 'store/selectors'
import useDebounce from 'hooks/useDebounce'
import { Autocomplete } from '@mui/material'
import { CustomShowMore } from 'ui-kit/components/CustomShowMore'
import { CustomTextField } from 'ui-kit/components/CustomTextField'
import { CommonResponseType } from 'types'
import { AutocompleteRenderGetTagProps } from '@mui/material/Autocomplete/Autocomplete'
import { Paragraph } from 'ui-kit/components/Typography/Typography'
import { getSubcontractorsForAutocomplete } from 'api/subcontractors'
import { SUBCONTRACTORS_PER_PAGE } from 'pages/SubcontractorsPage/config'
import { SubcontractorType } from 'slices/subcontractor'
import { CustomRemovedBox } from 'ui-kit/components/CustomRemovedBox'
import { getOptions } from 'lib/common'
import { AutocompletePropsType, ItemForAutocompleteType } from '.'
import Icons from 'assets/icons'

import s from 'components/CommonAutoComplete/styles.module.scss'

export const SubcontractorsAutoComplete = ({
    label,
    handleBlur,
    errorText,
    currentValue,
    readOnly = false,
    textFieldClassName,
    isSearch = false,
    disableClearable = false,
    textFieldPlaceholder,
    width = '100%',
    onChangeCallback,
    onDeleteItemClick,
    multiple,
}: AutocompletePropsType) => {
    const dispatch = useAppDispatch()
    const user = useAppSelector(currentUserSelect)
    const [subcontractors, setSubcontractors] = useState<SubcontractorType[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [count, setCount] = useState(0)
    const debouncedSearch = useDebounce(search, 500)

    const onInputChange = (event: any, newInputValue: string) => {
        setSearch(newInputValue)
        setPage(1)
    }

    const disabledCondition = count <= page * SUBCONTRACTORS_PER_PAGE || disableClearable

    const isOptionEqualToValue = (option: ItemForAutocompleteType, value: ItemForAutocompleteType) =>
        option?.value === value?.value

    const onShowMoreClick = (e: React.MouseEvent<HTMLParagraphElement>) => {
        e.preventDefault()
        setPage((prev) => prev + 1)
        getAllValues(page + 1)
    }

    const getAllValues = async (currentPage: number) => {
        setIsLoading(true)
        try {
            const res: CommonResponseType<SubcontractorType> = await dispatch(
                getSubcontractorsForAutocomplete({ page: currentPage, search, customerId: user?.customerId ?? '' }),
            ).unwrap()

            setCount(res?.count)
            setSubcontractors((prev) => (currentPage === 1 ? res.rows : [...prev, ...res.rows]))
        } catch (error: any) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!user || search) {
            return
        }
        getAllValues(page)
    }, [user, search])

    useEffect(() => {
        if (debouncedSearch) {
            getAllValues(page)
        }
    }, [debouncedSearch])

    const getOptionLabel = useCallback((option: ItemForAutocompleteType) => {
        return option?.label ?? ''
    }, [])

    const renderOption = useCallback(
        (props: React.HTMLAttributes<HTMLLIElement>, option: ItemForAutocompleteType) => {
            const currentSubcontractor = subcontractors.find((value) => value.id === option?.value)

            if (option.value === 'Not found') {
                return (
                    <li style={{ pointerEvents: 'none' }} {...props}>
                        <Icons.NotFoundSVG color="#848a9b" style={{ marginRight: '.4rem' }} />
                        <Paragraph size={'s'} color="grey">
                            Not found
                        </Paragraph>
                    </li>
                )
            }

            return (
                <li {...props}>
                    <Paragraph size={'s'} style={{ marginRight: '.4rem' }}>
                        {option?.label}
                    </Paragraph>
                    <div className={s.status}>
                        {currentSubcontractor?.approvedByProcurement && (
                            <Paragraph size={'xxs'} className={s.approved}>
                                Approved
                            </Paragraph>
                        )}
                    </div>
                </li>
            )
        },
        [subcontractors],
    )

    const renderTags = useCallback(
        (value: ItemForAutocompleteType[], getTagProps: AutocompleteRenderGetTagProps) => {
            if (onDeleteItemClick) {
                return ''
            }

            if (value?.length === 1) {
                return (
                    <Paragraph
                        size={'s'}
                        style={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {value[0]?.label}
                    </Paragraph>
                )
            } else {
                return (
                    <Paragraph size={'s'} style={{ display: 'flex', gap: '.4rem' }}>
                        {value?.length}
                        <Paragraph size={'s'} color={'grey'}>
                            (subcontractors)
                        </Paragraph>
                    </Paragraph>
                )
            }
        },
        [onDeleteItemClick],
    )

    const getPlaceholder = useMemo(() => {
        setSearch('')
        if (multiple) {
            return currentValue?.length ? '' : textFieldPlaceholder
        }
        return !Array.isArray(currentValue) && currentValue?.value ? '' : textFieldPlaceholder
    }, [multiple, currentValue])

    return (
        <>
            <Autocomplete
                value={currentValue}
                readOnly={readOnly}
                onChange={(event, newValue) => {
                    onChangeCallback(newValue as ItemForAutocompleteType[] & ItemForAutocompleteType, subcontractors)
                    setSearch('')
                }}
                openOnFocus={true}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        flexWrap: 'nowrap',
                        padding: 0,
                    },
                }}
                multiple={multiple}
                inputValue={search}
                onInputChange={onInputChange}
                options={
                    subcontractors?.length
                        ? getOptions(subcontractors, 'id', 'name')
                        : [{ value: 'Not found', label: 'Not found' }]
                }
                freeSolo
                renderTags={renderTags}
                getOptionLabel={(option) => getOptionLabel(option as ItemForAutocompleteType)}
                renderInput={(params) => (
                    <CustomTextField
                        label={label}
                        onBlur={handleBlur}
                        params={params}
                        errorText={errorText}
                        loading={isLoading}
                        className={textFieldClassName}
                        placeholder={getPlaceholder}
                        isSearch={isSearch}
                    />
                )}
                renderOption={renderOption}
                disableCloseOnSelect={multiple}
                filterOptions={(x) => x}
                limitTags={1}
                style={{ width }}
                loading={isLoading}
                isOptionEqualToValue={isOptionEqualToValue}
                disableClearable={disableClearable}
                PaperComponent={({ children }) => (
                    <CustomShowMore
                        onShowMoreClick={onShowMoreClick}
                        children={children}
                        text={'Show more'}
                        disabled={disabledCondition}
                    />
                )}
            />
            {onDeleteItemClick &&
                multiple &&
                currentValue?.map((item) => {
                    return (
                        <CustomRemovedBox
                            id={item?.value ?? ''}
                            title={item?.label ?? ''}
                            onDeleteClick={onDeleteItemClick}
                        />
                    )
                })}
        </>
    )
}
